import React, { useCallback, useRef, useState, type ReactNode } from 'react';

import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import { cx, type Styleable } from '../../variants';
import { useListboxKeyboardNav } from './useListboxKeyboardNav';

/**
 * EAA-1804: four portal components hand-roll the same single-select searchable
 * `role="listbox"` — `MoveOrganizationModal`, `Hierarchy/MoveOrgDialog`,
 * `ActingOrgPicker` and `RoleMap/AddRoleCombobox`. Each re-implements the same
 * `role="option"` row, `aria-selected`, a disabled/"blocked" row carrying a
 * reason, and (in `AddRoleCombobox`) roving ArrowUp/Down/Home/End/Enter
 * keyboard nav. This is that primitive, factored from what those four
 * actually share — see the mapping table in the Listbox PR/changeset for what
 * each one keeps doing itself (search box, loading skeleton rows, popover
 * trigger chrome, footnote copy) because it is caller-specific, not
 * listbox-specific.
 *
 * EAA-1839: those four were finally migrated here, and two MORE hand-rolls
 * written after this file shipped came with them — `RoleCoverage/
 * RolePickerPopover` and `ScopedAccounts/InlineLevelPicker` (the latter with a
 * raw `<input type="radio">` per option). Six copies, because `Listbox` was
 * never re-exported from the portal's `@/components/ui` barrel, so nobody
 * browsing the kit could see it existed. Three API gaps that had made the
 * migration impossible were closed at the same time: `optionClassName`,
 * `optionsUnstyled`, and the controlled-mode focus bug — see each below.
 */

export interface ListboxItem<T> {
    id: string;
    data: T;
    disabled?: boolean;
    /** Shown in place of the default secondary line when `disabled` is true. */
    disabledReason?: ReactNode;
}

export interface ListboxRenderState {
    selected: boolean;
    active: boolean;
    disabled: boolean;
}

export interface ListboxProps<T> extends Styleable {
    items: ListboxItem<T>[];
    selectedId?: string | null;
    onSelect: (item: ListboxItem<T>) => void;

    /**
     * Full row content. When omitted, the row falls back to `getPrimaryText`/
     * `getSecondaryText` (a plain two-line row) — every current call site
     * carries its own leading icon (avatar tile, checkmark, checkbox), so all
     * four use `renderItem`; the default two-line path is here for a future
     * caller with a genuinely plain row.
     */
    renderItem?: (item: ListboxItem<T>, state: ListboxRenderState) => ReactNode;
    getPrimaryText?: (item: ListboxItem<T>) => ReactNode;
    getSecondaryText?: (item: ListboxItem<T>) => ReactNode;

    /** Defaults to the kit's `common.noData` copy via `useUIText()`. */
    emptyState?: ReactNode;
    'aria-label'?: string;

    /**
     * Controlled highlighted option, for the COMBOBOX case where DOM focus
     * stays on an external search input (e.g. `AddRoleCombobox`) rather than
     * moving into the list. Pair with `onActiveIdChange` and bind the input's
     * `aria-activedescendant` to the id this reports. Left uncontrolled (the
     * default), the list manages its own roving `tabIndex` and DOM focus —
     * the shape `MoveOrganizationModal`/`MoveOrgDialog`/`ActingOrgPicker` want,
     * none of which route keyboard input through an outside field.
     */
    activeId?: string | null;
    onActiveIdChange?: (id: string | null) => void;

    /**
     * EAA-1839: per-option class hook, resolved per row and merged AFTER
     * `ui-listbox__option`, so a caller's rule wins on equal specificity — the
     * same merge contract `Styleable` gives the root. Without it a consuming app
     * can only reach an option through a descendant selector into this
     * component's internals, which the repo conventions forbid outright. That
     * gap is why all six portal call sites this file was extracted for still
     * had their own hand-rolled `role="option"` button long after it shipped.
     */
    optionClassName?: (item: ListboxItem<T>, state: ListboxRenderState) => string | undefined;

    /**
     * EAA-1839: drop the default inline option palette (background, colour,
     * padding, gap, cursor) so the caller's stylesheet owns the row chrome. The
     * inline defaults exist so an app that styles nothing still renders a
     * legible listbox — but inline styles beat class rules, so a caller that
     * DOES have real row CSS (hover, a brand-tinted selected state, a blocked
     * state, a fixed row height) could not restore any of it without
     * `!important`. Pass this together with `optionClassName`. The semantics —
     * role, aria, disabled, roving `tabIndex`, keyboard nav — are identical
     * either way; only the inline paint is withheld.
     */
    optionsUnstyled?: boolean;
}

function firstEnabledId<T>(items: ListboxItem<T>[]): string | null {
    return items.find((item) => !item.disabled)?.id ?? null;
}

export function Listbox<T>({
    items,
    selectedId = null,
    onSelect,
    renderItem,
    getPrimaryText,
    getSecondaryText,
    emptyState,
    'aria-label': ariaLabel,
    activeId: controlledActiveId,
    onActiveIdChange,
    optionClassName,
    optionsUnstyled,
    className,
    style,
}: ListboxProps<T>): React.ReactElement {
    const colors = useUIColors();
    const text = useUIText();
    const optionRefs = useRef(new Map<string, HTMLButtonElement>());

    // Uncontrolled fallback: a caller that never passes `activeId` (the three
    // plain-modal-list call sites) still gets roving keyboard nav for free —
    // this is new behavior for them, additive to what they hand-rolled today.
    const [internalActiveId, setInternalActiveId] = useState<string | null>(
        () => selectedId ?? firstEnabledId(items)
    );
    const isControlled = controlledActiveId !== undefined;
    const requestedActiveId = isControlled ? controlledActiveId : internalActiveId;

    /**
     * EAA-1839: the active row must always be one that is actually rendered.
     * `internalActiveId` is seeded ONCE from `selectedId`, but every call site
     * feeds `items` from a search box, so the seeded row routinely filters out —
     * and with it the only option carrying `tabIndex={0}`, which left the whole
     * list unreachable by Tab the moment the user typed anything. Falling back
     * to the first enabled row keeps exactly one tab stop at all times.
     */
    const requestedIsRendered =
        requestedActiveId !== null && items.some((item) => item.id === requestedActiveId);
    const activeId = requestedIsRendered ? requestedActiveId : firstEnabledId(items);

    const setActiveId = useCallback(
        (id: string | null) => {
            if (!isControlled) setInternalActiveId(id);
            onActiveIdChange?.(id);
            /**
             * EAA-1839: only the roving (uncontrolled) mode owns DOM focus. In
             * controlled mode focus stays in the caller's own search input by
             * contract — see `activeId`'s doc above — so pulling focus into the
             * option here broke the exact combobox shape that mode exists for:
             * the next keystroke after an arrow key went to the list, not the
             * field the user was typing in.
             */
            if (id && !isControlled) optionRefs.current.get(id)?.focus();
        },
        [isControlled, onActiveIdChange]
    );

    const handleKeyDown = useListboxKeyboardNav({
        items,
        activeId,
        onActiveIdChange: setActiveId,
        onSelect,
    });

    if (items.length === 0) {
        return (
            <div
                className={cx('ui-listbox', 'ui-listbox--empty', className)}
                role="listbox"
                aria-label={ariaLabel}
                style={style}
            >
                {emptyState ?? (
                    <p style={{ color: colors.textSecondary, fontSize: 13, margin: 0 }}>
                        {text.common.noData}
                    </p>
                )}
            </div>
        );
    }

    return (
        <div
            className={cx('ui-listbox', className)}
            role="listbox"
            aria-label={ariaLabel}
            onKeyDown={handleKeyDown}
            style={{ display: 'flex', flexDirection: 'column', ...style }}
        >
            {items.map((item) => {
                const selected = item.id === selectedId;
                const active = item.id === activeId;
                const disabled = Boolean(item.disabled);
                const state: ListboxRenderState = { selected, active, disabled };

                return (
                    <button
                        key={item.id}
                        ref={(node) => {
                            if (node) optionRefs.current.set(item.id, node);
                            else optionRefs.current.delete(item.id);
                        }}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        aria-disabled={disabled || undefined}
                        disabled={disabled}
                        tabIndex={isControlled ? -1 : active ? 0 : -1}
                        className={cx('ui-listbox__option', optionClassName?.(item, state))}
                        onClick={() => {
                            if (disabled) return;
                            setActiveId(item.id);
                            onSelect(item);
                        }}
                        onFocus={() => setActiveId(item.id)}
                        style={
                            optionsUnstyled
                                ? undefined
                                : {
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      width: '100%',
                                      padding: '8px 10px',
                                      border: 'none',
                                      background: selected ? colors.accentLight : 'transparent',
                                      color: disabled ? colors.textLight : colors.text,
                                      cursor: disabled ? 'not-allowed' : 'pointer',
                                      textAlign: 'left',
                                      font: 'inherit',
                                  }
                        }
                    >
                        {renderItem ? (
                            renderItem(item, state)
                        ) : (
                            <span
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    minWidth: 0,
                                }}
                            >
                                <span style={{ fontSize: 13, fontWeight: 500 }}>
                                    {getPrimaryText?.(item)}
                                </span>
                                <span style={{ fontSize: 12, color: colors.textSecondary }}>
                                    {disabled ? item.disabledReason : getSecondaryText?.(item)}
                                </span>
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export default Listbox;
